/**
 * Helpers for labeling step cache badges in the run details panel.
 */

import type { WorkflowRunStepMetadataDto } from '../hooks/useWorkflowRun';
import { formatDuration } from './run-formatters';

type StepCacheBadgeTone = 'cached' | 'recovery' | 'playbook' | 'agent';

type StepCacheBadge = {
  label: string;
  tooltip: string;
  tone: StepCacheBadgeTone;
};

const getAgentReasonText = (cacheOutcomeReason: string | undefined): string =>
  cacheOutcomeReason === 'no_prior_cache'
    ? 'No previous cache'
    : cacheOutcomeReason === 'cache_invalid'
      ? 'Cache was invalid'
      : cacheOutcomeReason === 'replay_failed'
        ? 'Cache replay failed'
        : cacheOutcomeReason === 'fallback_provider_fault'
          ? 'AI provider failed during the agent fallback — not a playbook failure'
          : cacheOutcomeReason === 'caching_disabled'
            ? 'Caching skipped'
            : cacheOutcomeReason === 'not_cacheable'
              ? 'Not cacheable'
              : cacheOutcomeReason === 'always_use_agent'
                ? 'Always uses agent'
                : 'Agent executed';

export function getStepCacheBadge(
  metadata: WorkflowRunStepMetadataDto | null
): StepCacheBadge | null {
  const cacheOutcome =
    typeof metadata?.cacheOutcome === 'string' ? metadata.cacheOutcome : undefined;
  const cacheHitSource =
    typeof metadata?.cacheHitSource === 'string' ? metadata.cacheHitSource : undefined;
  const cacheOutcomeReason =
    typeof metadata?.cacheOutcomeReason === 'string' ? metadata.cacheOutcomeReason : undefined;

  if (!cacheOutcome) return null;

  if (cacheOutcome === 'hit') {
    if (cacheHitSource === 'l2_gap') {
      const outcomes = metadata?.stepOutcomes;
      const completed = outcomes?.length ? outcomes.filter((o) => o.success).length : undefined;
      return {
        label: completed !== undefined ? `Playbook ${completed}/${outcomes!.length}` : 'Playbook',
        tooltip:
          'The playbook session executed this step (no single deterministic cache entry covered it), and every action succeeded without escalating to a fresh agent.',
        tone: 'playbook',
      };
    }

    if (cacheHitSource === 'recovery') {
      return {
        label: 'Heal',
        tooltip:
          'Deterministic cache heal repaired the failed cached action, then replay finished.',
        tone: 'recovery',
      };
    }

    if (cacheHitSource === 'wait_shortcut' || metadata?.waitShortcut === true) {
      return {
        label: 'Wait shortcut',
        tooltip: 'Resolved from the wait-only cache shortcut without running the full agent.',
        tone: 'cached',
      };
    }

    return {
      label: 'Cached',
      tooltip: 'Step completed from deterministic cache replay.',
      tone: 'cached',
    };
  }

  return {
    label: 'Agent',
    tooltip: getAgentReasonText(cacheOutcomeReason),
    tone: 'agent',
  };
}

export type StepExecutionPhases = {
  /** e.g. "Playbook 4/4 (1m 40s)" */
  l2Label: string;
  /** Why the step left the playbook, e.g. "confirmed-failed on 1 assertion(s)" */
  ejectReason?: string;
  /** e.g. "Agent (17 turns, 2m 1s)" */
  l3Label: string;
};

/**
 * Derive the playbook → agent phase breakdown for a step where the cached
 * playbook ran first and then cascaded to a fresh agent. Without this the
 * step card silently merges the two: the playbook rows are the playbook's
 * work while the turn/assumption badges are the agent's. Returns null for
 * non-cascaded steps (pure hits, pure agent runs, and historical runs
 * without the metadata).
 */
export function getStepExecutionPhases(
  metadata: WorkflowRunStepMetadataDto | null
): StepExecutionPhases | null {
  if (metadata?.cacheOutcome !== 'fallback') return null;
  const outcomes = metadata.stepOutcomes;
  if (!outcomes?.length) return null;

  const completed = outcomes.filter((o) => o.success).length;
  const l2Duration =
    metadata.replayDurationMs !== undefined
      ? ` (${formatDuration(metadata.replayDurationMs)})`
      : '';

  const turns = metadata.turnStats?.turns?.length ?? 0;
  const l3Duration = metadata.turnStats?.totalDurationMs;
  const l3Label =
    turns > 0
      ? `Agent (${turns} turn${turns !== 1 ? 's' : ''}${
          l3Duration !== undefined ? `, ${formatDuration(l3Duration)}` : ''
        })`
      : 'Agent';

  return {
    l2Label: `Playbook ${completed}/${outcomes.length}${l2Duration}`,
    ejectReason: metadata.cacheOutcomeReason,
    l3Label,
  };
}
